/**
 * DIE OFFENE BUEHNE UEBERLEBT DAS NEULADEN (Rafael, 14.10.2026 06:41Z — nicht zum ersten Mal).
 *
 * Gemessen: Der Assistent öffnete ihm um 06:38:05Z die RLB-Bühne (ok, sichtbar). Um 06:40:18Z
 * lud das Fenster neu (cui-usage: stage_leave reason=pagehide). Danach stand links nur noch der
 * Einleitungstext, die Frage der Bühne war weg. `offeneBuehne` war reiner React-Zustand. Ein
 * Neuladen kommt von F5, aber auch automatisch nach jedem Frontend-Push (SessionStore buildId).
 *
 * Der Server merkt sich je Person und Fenster, welche Bühne offen ist
 * (server/routes/guide-fenster-buehne.ts). Beim Laden holt der Guide sie zurück. Die
 * Fensterkennung ist dieselbe wie bei den Entwürfen (sessionStorage, überlebt F5 im selben Tab,
 * neu für jedes neue Fenster).
 *
 * Reihenfolge, die zählt: Erst wenn das Zurückholen abgeschlossen ist, wird gemeldet. Sonst
 * schriebe der leere Anfangszustand („nichts offen“) den gemerkten Stand weg, bevor er gelesen ist.
 */
import { useEffect, useRef } from 'react';
import { GuideHttpFehler, guideNetzFehler, mitNetzWiederholung } from './guideNetz';
import { fensterKennung } from './gesicherterEntwurf';

export const FENSTER_BUEHNE_API = '/api/preferences/guide-fenster-buehne';

export async function gemerkteBuehneLesen(fenster: string): Promise<string | null> {
  const r = await fetch(`${FENSTER_BUEHNE_API}?fenster=${encodeURIComponent(fenster)}`, { signal: AbortSignal.timeout(8000) }); // trust-boundary:ok eigene Route; buehneId wird unten auf string|null geprüft
  if (!r.ok) throw new GuideHttpFehler(r.status);
  const d = await r.json() as { buehneId?: unknown };
  if (d.buehneId !== null && typeof d.buehneId !== 'string') throw new Error('Antwort ohne buehneId');
  return d.buehneId;
}

export async function buehneMerken(fenster: string, buehneId: string | null): Promise<void> {
  const r = await fetch(FENSTER_BUEHNE_API, { // trust-boundary:ok eigene Route, Körper vom Server per zod geprüft, Antwort ungelesen (nur Status)
    method: 'PUT', headers: { 'content-type': 'application/json' }, keepalive: true,
    body: JSON.stringify({ fenster, buehneId }), signal: AbortSignal.timeout(8000),
  });
  if (!r.ok) throw new GuideHttpFehler(r.status);
}

export interface FensterBuehneOptionen {
  /** Kennung der gerade offenen Bühne, `null` = keine. */
  offeneId: string | null;
  /** Öffnet eine Bühne. `true` nur, wenn sie danach wirklich offen ist. */
  oeffnen: (id: string) => Promise<boolean>;
  /** Kein Zurückholen und kein Melden (Schatten-Client, Deep-Link, Partneransicht ohne Guide). */
  aus?: boolean;
  /** Sichtbarer Fehler. Ein stilles Scheitern hiesse wieder: Bühne weg, niemand weiß warum. */
  onFehler: (text: string) => void;
}

export function useFensterBuehne({ offeneId, oeffnen, aus = false, onFehler }: FensterBuehneOptionen): void {
  const bereit = useRef(false);
  /** Was der Server gerade für dieses Fenster weiß. `undefined` = noch nicht gelesen. */
  const gemeldet = useRef<string | null | undefined>(undefined);
  const oeffnenRef = useRef(oeffnen); oeffnenRef.current = oeffnen;
  const fehlerRef = useRef(onFehler); fehlerRef.current = onFehler;
  const offeneRef = useRef(offeneId); offeneRef.current = offeneId;

  const lauf = useRef<AbortController | null>(null);
  const schreibKette = useRef<Promise<unknown>>(Promise.resolve());
  const gemeldeterFehler = useRef(false);
  const melden = (id: string | null) => {
    const vorherigerLauf = lauf.current; vorherigerLauf?.abort();
    if (id === gemeldet.current && !vorherigerLauf) return;
    const stop = new AbortController(); lauf.current = stop;
    // Serialize writes; a superseded request can finish, but is never replayed.
    schreibKette.current = schreibKette.current.then(async () => {
      if (stop.signal.aborted) return;
      const ok = await mitNetzWiederholung(async () => { await buehneMerken(fensterKennung(), id); return true; }, stop.signal, e => {
        gemeldeterFehler.current = true;
        fehlerRef.current(`Die offene Bühne ist für dieses Fenster noch nicht gemerkt (${guideNetzFehler(e)}).`);
      });
      if (ok && !stop.signal.aborted) {
        gemeldet.current = id;
        if (gemeldeterFehler.current) { gemeldeterFehler.current = false; fehlerRef.current(''); }
        if (lauf.current === stop) lauf.current = null;
      }
    });
  };
  useEffect(() => { if (aus) lauf.current?.abort(); return () => lauf.current?.abort(); }, [aus]);

  useEffect(() => {
    if (aus) return;
    let aktiv = true;
    void (async () => {
      let id: string | null = null;
      try { id = await gemerkteBuehneLesen(fensterKennung()); } catch (e) {
        if (aktiv) fehlerRef.current(`Die zuletzt offene Bühne ließ sich nicht wiederherstellen (${e instanceof Error ? e.message : String(e)}).`);
      }
      if (!aktiv) return;
      gemeldet.current = id;
      // Ist inzwischen schon etwas offen (Auftrag des Assistenten, Klick), gewinnt das.
      if (id && offeneRef.current === null) {
        const ok = await oeffnenRef.current(id);
        if (!aktiv) return;
        bereit.current = true;
        // Die Bühne gibt es nicht mehr oder sie ließ sich nicht öffnen: nicht bei jedem Laden neu versuchen.
        // Der Grund steht schon sichtbar da (oeffnen meldet ihn selbst).
        if (!ok) { if (offeneRef.current === null) melden(null); else melden(offeneRef.current); return; }
        // Geöffnet. `offeneRef` kann noch den Stand VOR dem Rendern tragen (null) — das ist kein Schließen.
        if (offeneRef.current !== null && offeneRef.current !== id) melden(offeneRef.current);
        return;
      }
      bereit.current = true;
      if (offeneRef.current !== gemeldet.current) melden(offeneRef.current);
    })();
    return () => { aktiv = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aus]);

  useEffect(() => {
    if (aus || !bereit.current) return;
    melden(offeneId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offeneId, aus]);
}
